import '@xyflow/react/dist/style.css';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, Collapse, IconButton, Menu, MenuItem, TextField, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import {
  Background,
  Connection,
  ConnectionLineType,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  reconnectEdge,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useBeforeUnload, useBlocker, useNavigate, useParams } from 'react-router-dom';
// @ts-ignore
import { v4 as uuid } from 'uuid';

import ActionDialog from '~components/ActionDialog';
import AsyncLoader from '~components/AsyncLoader';
import EmptyState from '~components/EmptyState';
import { useSiteLayout } from '~components/SiteLayout';
import { createContactFlow, getContactFlow, updateContactFlow } from '~pages/SipConfiguration/api';
import {
  ContactFlowAction,
  ContactFlowActionType,
  ContactFlowActionsSummary,
  ContactFlowData,
  ContactFlowInfo,
  ContactFlowVersion,
  GetContactFlowDataDecoder,
  GetContactFlowDataEncoder,
} from '~pages/SipConfiguration/domain';
import { useNotification } from '~providers/NotificationProvider';
import Routes from '~providers/RouteProvider/Routes';
import { APIError, UnsupportedStructureError } from '~services/Errors';
import { deepEqual } from '~utils/Functions';

import JSONEditorDialog from './JSONEditorDialog';
import { ContactFlowNode, ContactFlowNodes, CreateableContactFlowNodes } from './Nodes';

function actionsToNodesAndEdges(actions: ContactFlowAction[]): { nodes: ContactFlowNode[]; edges: Edge[] } {
  return {
    nodes: actions.map(({ id, type, description, parameter, next }) => ({
      id,
      // fallback to default node if not a ContactFlowNode
      ...(type in ContactFlowNodes && { type }),
      data: {
        type,
        description,
        parameter: Object.entries(parameter ?? {}).reduce((p, [k, v]) => (v ? { ...p, [k]: v } : p), {}),
        sourceHandles: next?.routes.map(({ selector }, i) => ({
          handleId: `${id}-${i}`,
          selector,
        })),
      },
      position: { x: 0, y: 0 },
    })),
    edges: actions.flatMap(({ id: source, next }) =>
      (next?.routes || []).map(({ actionId: target }, i) => {
        const sourceHandle = `${source}-${i}`;
        return {
          id: `${sourceHandle}-${target}`,
          source,
          target,
          sourceHandle,
        };
      }),
    ),
  };
}

function nodesAndEdgesToActions(nodes: ContactFlowNode[], edges: Edge[]): ContactFlowActionsSummary {
  return {
    actions: nodes.slice(1).map(({ id, data: { type, parameter, description = '', sourceHandles } }) => ({
      id,
      type,
      description,
      parameter,
      next: sourceHandles && {
        routes: sourceHandles.map(({ handleId, selector }) => ({
          actionId: edges.find((e) => e.sourceHandle === handleId)?.target ?? '',
          selector,
        })),
      },
    })),
    startActionId: edges.find(({ source }) => source === nodes[0].id)?.target ?? '',
  };
}

const elk = new ELK();
const layoutOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.layered.spacing.edgeNodeBetweenLayers': '80',
  'elk.spacing.edgeEdge': '60',
  'elk.spacing.nodeNode': '60',
  'elk.spacing.edgeNode': '60',
  'elk.layered.nodePlacement.strategy': 'SIMPLE',
};

async function relayoutNodes(nodes: ContactFlowNode[], edges: Edge[]) {
  const graph = {
    id: 'root',
    layoutOptions,
    children: nodes.map((n) => {
      const sourcePorts = n.data.sourceHandles?.map((s) => ({
        id: s.handleId,
        properties: {
          side: 'EAST',
        },
      }));

      return {
        id: n.id,
        width: n.width ?? n.measured?.width ?? 150,
        height: n.height ?? n.measured?.height ?? 50,

        // ⚠️ we need to tell elk that the ports are fixed, in order to reduce edge crossings
        properties: {
          'org.eclipse.elk.portConstraints': 'FIXED_ORDER',
        },
        // we are also passing the id, so we can also handle edges without a sourceHandle or targetHandle option
        ports: [
          {
            id: n.id,
            properties: {
              side: 'WEST',
            },
          },
          ...(sourcePorts ?? []),
        ],
      };
    }),
    edges: edges.map((e) => ({
      id: e.id,
      targets: [e.targetHandle || e.target],
      sources: [e.sourceHandle || e.source],
    })),
  };

  const layoutedGraph = await elk.layout(graph);

  const layoutedNodes = nodes.map((node) => {
    const layoutedNode = layoutedGraph.children?.find((lgNode) => lgNode.id === node.id);
    return {
      ...node,
      position: {
        x: layoutedNode?.x ?? 0,
        y: layoutedNode?.y ?? 0,
      },
    };
  });

  return layoutedNodes;
}

type ErrorMessage = {
  text: string;
  subText: string;
};
const StartingPointNode: ContactFlowNode = {
  id: 'start-entry-point',
  type: ContactFlowActionType.Entry,
  data: {
    type: ContactFlowActionType.Entry,
    parameter: {},
  },
  position: { x: 0, y: 0 },
};

type LayoutFlowState = 'fetching' | 'should-relayout' | 'relayouting' | 'should-adjust-view' | 'none';

function LayoutFlow() {
  const { contactFlowId } = useParams<{ contactFlowId: string | undefined }>();
  const navigate = useNavigate();
  const { pushNotification } = useNotification();
  const contactFlowInfoForm = useForm<ContactFlowInfo>({
    defaultValues: {
      version: ContactFlowVersion,
    },
  });
  const {
    control,
    getValues,
    reset,
    handleSubmit,
    formState: { isDirty: isContactFlowInfoDirty },
  } = contactFlowInfoForm;
  const [nodes, setNodes, onNodesChange] = useNodesState<ContactFlowNode>(contactFlowId ? [] : [StartingPointNode]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [initialContactFlowActionsSummary, setInitialContactFlowActionsSummary] = useState<ContactFlowActionsSummary>(
    nodesAndEdgesToActions(nodes, edges),
  );
  const isActionsSummaryDirty = useMemo(
    () => !deepEqual(initialContactFlowActionsSummary, nodesAndEdgesToActions(nodes, edges)),
    [initialContactFlowActionsSummary, nodes, edges],
  );
  const isDirty = isContactFlowInfoDirty || isActionsSummaryDirty;
  const { viewportInitialized, screenToFlowPosition, fitView } = useReactFlow<ContactFlowNode, Edge>();
  const updateNodeInternals = useUpdateNodeInternals();
  const nodesInitialized = useNodesInitialized();
  const [openEditor, setOpenEditor] = useState<boolean>(false);
  const [state, setState] = useState<LayoutFlowState>(contactFlowId ? 'fetching' : 'none');
  const [showMoreInfo, setShowMoreInfo] = useState<boolean>(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const [publish, setPublish] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage>();

  const setContactFlowData = (cf: ContactFlowData) => {
    reset(cf);
    const { nodes, edges } = actionsToNodesAndEdges(cf.actions);
    // you need to create a new starting point node object
    // else it wont do relayout
    nodes.unshift({ ...StartingPointNode });
    edges.unshift({
      id: StartingPointNode.id,
      source: StartingPointNode.id,
      sourceHandle: StartingPointNode.id,
      target: cf.startActionId,
    });
    setNodes(nodes);
    setEdges(edges);
    return { nodes, edges };
  };

  useEffect(() => {
    if (contactFlowId) {
      (async () => {
        try {
          const cf = await getContactFlow(+contactFlowId);
          const { nodes, edges } = setContactFlowData(cf);
          setInitialContactFlowActionsSummary(nodesAndEdgesToActions(nodes, edges));
          setState('should-relayout');
        } catch (e) {
          if (e instanceof APIError) {
            setError({ text: 'Unable to request data from backend', subText: e.message });
          }
          if (e instanceof UnsupportedStructureError) {
            setError({ text: 'Data from backend Invalid', subText: 'Unable to decode response' });
          }
        }
      })();
    }
  }, [contactFlowId]);

  useEffect(() => {
    if (state == 'should-relayout' && nodesInitialized) {
      console.log('RELAYOUT');
      setState('relayouting');
      (async () => {
        try {
          setNodes(await relayoutNodes(nodes, edges));
          setState('should-adjust-view');
        } catch {
          pushNotification('error', 'Relayout failed! Invalid contact flow.');
        }
      })();
    }
  }, [state, nodesInitialized]);

  useEffect(() => {
    if (state == 'should-adjust-view' && nodesInitialized) {
      setState('none');
      fitView({
        nodes: [StartingPointNode],
        maxZoom: 0.5,
      });
    }
  }, [state, nodesInitialized]);

  const onConnect = (conn: Connection) => {
    setEdges((eds) => addEdge(conn, eds));
    updateNodeInternals([conn.source, conn.target]);
  };
  const onReconnect = (e: Edge, conn: Connection) => setEdges((eds) => reconnectEdge(e, conn, eds));

  const blocker = useBlocker(() => !publish && isDirty);
  useEffect(() => {
    if (blocker.state == 'blocked') {
      if (confirm('Changes you made may not be saved. Would you like to proceed?')) {
        blocker.proceed();
      }
    }
  }, [blocker]);

  useBeforeUnload((e) => {
    if (isDirty) {
      e.preventDefault();

      // Included for legacy support, e.g. Chrome/Edge < 119
      e.returnValue = true;
    }
  });

  const errorDisplay = error && <EmptyState type='error' text={error.text} />;

  return (
    <AsyncLoader isLoading={state == 'fetching'} error={errorDisplay}>
      <FormProvider {...contactFlowInfoForm}>
        <ActionDialog
          open={publish}
          title='Are you sure you want to do this?'
          content='You are about to publish this contact flow.'
          primaryActionTitle='Publish'
          onAccept={handleSubmit(async (data) => {
            const cf: ContactFlowData = {
              ...data,
              ...nodesAndEdgesToActions(nodes, edges),
            };
            try {
              if (contactFlowId) {
                await updateContactFlow(+contactFlowId, cf);
                pushNotification('success', 'Success updating contact flow');
              } else {
                await createContactFlow(cf);
                pushNotification('success', 'Success creating new contact flow');
              }
              navigate(`${Routes.sipConfiguration.path}?show=ContactFlows`);
            } catch (e) {
              pushNotification('error', 'Invalid contact flow data');
            }
          })}
          onClose={() => {
            setPublish(false);
          }}
        />
        <JSONEditorDialog
          open={openEditor}
          onOpen={(editor) => {
            const cf: ContactFlowData = {
              ...getValues(),
              ...nodesAndEdgesToActions(nodes, edges),
            };
            editor.set(GetContactFlowDataEncoder.runWithException(cf));
          }}
          onSubmit={(editor) => {
            const data = GetContactFlowDataDecoder.run(editor.get());
            if (data.ok) {
              setContactFlowData(data.result);
              setTimeout(() => setState('should-relayout'), 1);
              setOpenEditor(false);
            } else {
              pushNotification('error', data.error.message);
            }
          }}
          onClose={() => setOpenEditor(false)}
        />
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              paddingTop: 6,
              paddingBottom: 2,
              paddingLeft: 3,
              paddingRight: 3,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                alignItems: 'end',
              }}>
              <IconButton onClick={() => setShowMoreInfo((p) => !p)} color='info' size='medium' aria-label='More'>
                {showMoreInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                }}>
                <Controller
                  control={control}
                  name='name'
                  defaultValue=''
                  render={({ field }) => (
                    <TextField {...field} fullWidth size='small' variant='outlined' label='Contact Flow Name' />
                  )}
                />
                <Collapse in={showMoreInfo}>
                  <Box sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Controller
                      control={control}
                      name='description'
                      defaultValue=''
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          size='small'
                          variant='outlined'
                          label='Description'
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name='tags'
                      defaultValue=''
                      render={({ field }) => (
                        <TextField {...field} fullWidth size='small' variant='outlined' label='Tags' />
                      )}
                    />
                  </Box>
                </Collapse>
              </Box>
              <Button
                disableElevation
                variant='contained'
                color='primary'
                disabled={state != 'none' || !isDirty}
                onClick={() => {
                  setPublish(true);
                }}>
                Publish
              </Button>
              <Button
                disableElevation
                variant='outlined'
                color='primary'
                onClick={() => {
                  navigate(`${Routes.sipConfiguration.path}?show=ContactFlows`);
                }}>
                Back
              </Button>
              <IconButton onClick={(e) => setMoreAnchorEl(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu anchorEl={moreAnchorEl} open={Boolean(moreAnchorEl)} onClose={() => setMoreAnchorEl(null)}>
                <MenuItem
                  onClick={() => {
                    setMoreAnchorEl(null);
                    setOpenEditor(true);
                  }}>
                  Edit JSON Template
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                flexShrink: 1,
                position: 'relative',
                width: '400px',
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                overflow: 'auto',
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  padding: 2,
                }}>
                <Typography style={{ whiteSpace: 'break-spaces' }} variant='body1' component='p' align='left' paragraph>
                  You can drag these nodes to the pane on the right.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {CreateableContactFlowNodes.map(({ title, type }, index) => {
                    return (
                      <Box
                        draggable
                        key={type}
                        sx={{
                          border: '1px solid',
                          borderRadius: 50,
                          paddingLeft: 2,
                          paddingRight: 2,
                          cursor: 'pointer',
                        }}
                        onDragStart={(event) => {
                          event.dataTransfer.setData('oberon/sip-contact-flow', type);
                          event.dataTransfer.effectAllowed = 'move';
                        }}>
                        {title}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            <ReactFlow
              fitView
              minZoom={0.2}
              proOptions={{ hideAttribution: true }}
              style={{ backgroundColor: '#F7F9FB' }}
              nodeTypes={ContactFlowNodes}
              nodes={nodes}
              edges={edges}
              defaultEdgeOptions={{
                type: ConnectionLineType.Bezier,
                style: { strokeWidth: 4 },
                focusable: true,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: blue[600],
                },
              }}
              connectionLineType={ConnectionLineType.Bezier}
              connectionLineStyle={{ strokeWidth: 6, stroke: blue[600], strokeDasharray: '12 8' }}
              connectionRadius={30}
              elevateEdgesOnSelect={true}
              onConnect={onConnect}
              onReconnect={onReconnect}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onEdgesDelete={(edges) => {
                updateNodeInternals(edges.map((edge) => [edge.target, edge.source]).flat());
              }}
              reconnectRadius={20}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (viewportInitialized) {
                  const type = e.dataTransfer.getData('oberon/sip-contact-flow');
                  const position = screenToFlowPosition({
                    x: e.clientX,
                    y: e.clientY,
                  });
                  const newNode: ContactFlowNode = {
                    id: uuid(),
                    type,
                    position,
                    data: { type, parameter: {}, isNew: true },
                  };
                  setNodes((nds) => nds.concat(newNode));
                }
              }}>
              <Background />
              <MiniMap
                ariaLabel='Contact Flow Minimap'
                position='bottom-right'
                pannable={true}
                maskColor='rgb(0, 0, 0, 0.4)'
              />
              <Controls position='bottom-left' />
            </ReactFlow>
          </Box>
        </Box>
      </FormProvider>
    </AsyncLoader>
  );
}

export default function ContactFlowDetails() {
  const setLayout = useSiteLayout();
  useEffect(() => {
    setLayout('fullScreen');
    return () => setLayout('default');
  }, []);

  return (
    <ReactFlowProvider>
      <LayoutFlow />
    </ReactFlowProvider>
  );
}
